import { useReducer, useEffect } from 'react';

import useEnv from '@zenbusiness/application-commons-env';
import Paper from '@zenbusiness/zui/Paper';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import usePlacesAutocomplete from 'use-places-autocomplete';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import useLoadScriptStatus from '#client/hooks/useLoadScript';
import { buildAddress } from '#client/utils/google-maps';

import { validateAddress } from './utils/validate';

const UPDATE = 'update';
const RESET = 'reset';
const RESET_ADDRESS = 'reset-address';
const FOCUS = 'focus';
const BLUR = 'blur';
const START_ASYNC = 'start-async';
const END_ASYNC = 'end-async';

const emptyAddress = {
  addressLine1: '',
  city: '',
  state: '',
  country: '',
  zip: ''
};

const ADDRESS_INVALID_MESSAGE = 'The address could not be found on the map. Please try again.';

const reducer = (state, action) => {
  switch (action.type) {
    case UPDATE:
      return {
        ...state,
        ...action.payload
      };
    case START_ASYNC:
      return {
        ...state,
        isLoading: true
      };
    case END_ASYNC:
      return {
        ...state,
        isLoading: false
      };
    case RESET:
      return {
        ...state,
        ...emptyAddress
      };
    case RESET_ADDRESS:
      return {
        ...state,
        ...emptyAddress,
        valid: false
      };
    case FOCUS:
      return {
        ...state,
        isDirty: true,
        isFocus: true
      };
    case BLUR:
      return {
        ...state,
        isFocus: false
      };

    default:
      return state;
  }
};

const CustomPaper = ({ children, ...props }) => {
  // check if any options to render
  if (children?.[2] === null) {
    return null;
  }
  return (
    <Paper
      elevation={8}
      {...props}
    >
      {children}
    </Paper>
  );
};

const GoogleMapField = ({
  disabled,
  placeholder = 'Address',
  validateEmpty = true,
  dataChangeHandler,
  onChangeInput,
  endAdornment,
  disableClearable = false,
  errorMessage = '',
  sizeError = 'xs',
  defaultValue = ''
}) => {
  const [address, dispatchAddress] = useReducer(reducer, {
    ...emptyAddress,
    inputValue: '',
    valid: !!defaultValue,
    isDirty: false,
    isFocus: false,
    isLoading: false
  });

  const isShowError =
    (address.inputValue.length > 0 || validateEmpty) &&
    address.isDirty &&
    !address.isFocus &&
    !address.valid &&
    !address.isLoading;

  const { init, ready, suggestions, value, setValue, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' }
    },
    debounce: 200,
    initOnMount: false
  });
  const { ZENBUSINESS_GOOGLE_MAPS_KEY } = useEnv();
  const googleMapScriptStatus = useLoadScriptStatus(
    `https://maps.googleapis.com/maps/api/js?key=${ZENBUSINESS_GOOGLE_MAPS_KEY}&libraries=places`
  );

  useEffect(() => {
    // Start usePlacesAutocomplete hook
    if (googleMapScriptStatus === 'ready' && !ready) {
      init();
    }
  }, [googleMapScriptStatus, init, ready]);

  const disableAddressField = disabled || !ready || googleMapScriptStatus !== 'ready';
  const isShowOptions = address.isFocus && suggestions?.status === 'OK';

  const onDataChanged = (newAddress) => {
    const { addressLine1, city, state, country, zip, valid } = newAddress;

    dataChangeHandler({
      valid,
      address: {
        addressLine1,
        city,
        state,
        country,
        zip
      }
    });
  };

  const handleUnSave = () => {
    dataChangeHandler({
      valid: false,
      address: emptyAddress
    });
  };

  const handleGoogleAddressChange = async (_, option) => {
    dispatchAddress({ type: START_ASYNC });
    const { description, place_id: placeId } = option || {};
    if (!placeId) {
      dispatchAddress({ type: RESET });
      return;
    }
    const googleAddress = await buildAddress(description, placeId);
    const valid = validateAddress(googleAddress);
    const { addressLine1, city, state, country, zip } = googleAddress;

    const newAddress = {
      addressLine1,
      city,
      state,
      country,
      zip,
      valid
    };

    onDataChanged(newAddress);
    dispatchAddress({ type: UPDATE, payload: newAddress });
    setTimeout(() => {
      dispatchAddress({ type: END_ASYNC });
    }, 100);
  };

  const handleBlurOrClickAway = () => {
    dispatchAddress({ type: BLUR });
    clearSuggestions();
    setValue('', false);
  };

  const handleFocus = () => {
    dispatchAddress({ type: FOCUS });
  };

  const handleClear = () => {
    const newAddress = {
      ...emptyAddress,
      valid: false
    };

    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
    setTimeout(() => {
      dispatchAddress({ type: END_ASYNC });
    }, 100);
  };

  const handleOtherReason = (event, newInputValue, reason) => {
    if (typeof onChangeInput === 'function') {
      dispatchAddress({ type: UPDATE, payload: { inputValue: newInputValue } });
      onChangeInput(newInputValue);
    }
    switch (reason) {
      case 'clear':
        handleClear();
        break;
      case 'input':
        if (value !== newInputValue || !value) {
          clearSuggestions();
          setValue(newInputValue, !!newInputValue);
          dispatchAddress({ type: RESET_ADDRESS });
          handleUnSave();
        }
        break;
      default:
        break;
    }
  };

  const renderOption = (props, option) => (
    <Text
      {...props}
      component="span"
      id={option?.place_id}
      key={option?.place_id}
    >
      <Text component="div">
        <Text component="span">{option?.description}</Text>
      </Text>
    </Text>
  );

  return (
    <Stack spacing="8px">
      <AutoCompleteInput
        autoHighlight
        blurOnSelect
        defaultValue={{
          description: defaultValue
        }}
        disableClearable={disableClearable}
        disabled={disableAddressField}
        freeSolo
        getOptionLabel={(option) => option?.description || ''}
        onChange={handleGoogleAddressChange}
        onInputChange={handleOtherReason}
        open={isShowOptions}
        openOnFocus
        options={suggestions?.data}
        PaperComponent={CustomPaper}
        renderInput={(params) => (
          <TextField
            {...params}
            error={isShowError}
            InputProps={{
              ...params.InputProps,
              endAdornment
            }}
            label={placeholder}
            onBlur={handleBlurOrClickAway}
            onFocus={handleFocus}
          />
        )}
        renderOption={renderOption}
      />
      {isShowError && (
        <Text
          color="red"
          component="div"
          variant={sizeError}
        >
          {errorMessage || ADDRESS_INVALID_MESSAGE}
        </Text>
      )}
    </Stack>
  );
};

export default GoogleMapField;

GoogleMapField.propTypes = {
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  disableClearable: PropTypes.bool,
  dataChangeHandler: PropTypes.func,
  endAdornment: PropTypes.node,
  errorMessage: PropTypes.string,
  sizeError: PropTypes.string,
  onChangeInput: PropTypes.func,
  validateEmpty: PropTypes.bool,
  defaultValue: PropTypes.string
};
